// PROPOSITO: menu de demostracion. Enseña el sistema real de Flor: varias opciones por momento del
// dia, TODAS igualadas en calorias, para que la alumna elija sin romper el plan.
// DEPENDENCIAS: src/types/dominio.
// NO OBVIO: las opciones de un mismo momento suman lo mismo a proposito. Es el modelo de
// equivalentes de Nutrium y es lo que Flor ya hace a mano: "da lo mismo el desayuno uno con la cena
// tres". Al intercambiar NO se recalcula ningun gramaje — ningun producto verificado lo hace.

import type { ComidaDelDia, MomentoComida, OpcionDeMenu } from '@/types/dominio'

interface Receta {
  nombre: string
  kcal: number
  p: number
  c: number
  g: number
  ingredientes: string
  preparacion: string
  etiquetas: string[]
}

const DESAYUNOS: Receta[] = [
  { nombre: 'Avena con plátano y mantequilla de maní', kcal: 420, p: 18, c: 58, g: 13, ingredientes: '60 g avena · 1 plátano · 15 g mantequilla de maní · 200 ml leche descremada', preparacion: 'Cocina la avena con la leche 5 minutos. Agrega el plátano en rodajas y la mantequilla de maní encima.', etiquetas: ['sin_lactosa_opcional'] },
  { nombre: 'Tostadas con huevo y palta', kcal: 418, p: 20, c: 40, g: 19, ingredientes: '2 rebanadas pan integral · 2 huevos · 1/2 palta · sal y limón', preparacion: 'Tuesta el pan. Cocina los huevos a la plancha. Muele la palta con limón y sal y arma encima.', etiquetas: [] },
  { nombre: 'Yogur griego con granola y berries', kcal: 415, p: 24, c: 52, g: 11, ingredientes: '200 g yogur griego natural · 40 g granola · 100 g frutos rojos · canela', preparacion: 'Mezcla todo en un bowl. La canela va al final.', etiquetas: [] },
  { nombre: 'Panqueques de avena y clara', kcal: 422, p: 26, c: 54, g: 10, ingredientes: '50 g avena · 3 claras · 1 huevo · 1 plátano · polvos de hornear', preparacion: 'Licúa todo. Cocina en sartén antiadherente, 2 minutos por lado.', etiquetas: ['sin_lactosa'] },
]

const ALMUERZOS: Receta[] = [
  { nombre: 'Pollo al horno con arroz y ensalada', kcal: 610, p: 48, c: 62, g: 16, ingredientes: '180 g pechuga · 80 g arroz crudo · lechuga, tomate y pepino · 1 cda aceite de oliva', preparacion: 'Pollo al horno 25 min a 200°C con pimentón y ajo. Arroz aparte. Ensalada aliñada con el aceite.', etiquetas: ['sin_gluten', 'sin_lactosa'] },
  { nombre: 'Salmón con puré rústico y brócoli', kcal: 615, p: 44, c: 52, g: 22, ingredientes: '160 g salmón · 300 g papas · 150 g brócoli · 1 cda aceite de oliva', preparacion: 'Salmón a la plancha 4 min por lado. Papas cocidas y aplastadas con su piel. Brócoli al vapor.', etiquetas: ['sin_gluten'] },
  { nombre: 'Carne magra con quinoa y verduras salteadas', kcal: 608, p: 46, c: 58, g: 18, ingredientes: '160 g posta · 70 g quinoa cruda · zapallo italiano, pimentón y cebolla', preparacion: 'Sella la carne. Quinoa cocida 15 min. Saltea las verduras al final con la carne.', etiquetas: ['sin_gluten', 'sin_lactosa'] },
  { nombre: 'Pasta integral con atún y tomate', kcal: 612, p: 42, c: 74, g: 14, ingredientes: '90 g pasta integral cruda · 1 lata atún al agua · salsa de tomate natural · albahaca', preparacion: 'Pasta al dente. Calienta el tomate con el atún escurrido. Mezcla y agrega albahaca.', etiquetas: ['sin_lactosa'] },
]

const CENAS: Receta[] = [
  { nombre: 'Tortilla de claras con espinaca y queso fresco', kcal: 390, p: 38, c: 18, g: 18, ingredientes: '4 claras · 1 huevo · 100 g espinaca · 50 g queso fresco', preparacion: 'Saltea la espinaca. Agrega los huevos batidos y el queso en cubos. Cuaja a fuego bajo.', etiquetas: ['sin_gluten'] },
  { nombre: 'Pollo desmenuzado con verduras al wok', kcal: 388, p: 42, c: 24, g: 13, ingredientes: '150 g pechuga · brócoli, zanahoria y champiñón · salsa de soya baja en sodio', preparacion: 'Cocina y desmenuza el pollo. Wok fuerte con las verduras 5 min. Une y agrega la soya.', etiquetas: ['sin_lactosa'] },
  { nombre: 'Ensalada tibia de atún y garbanzos', kcal: 392, p: 36, c: 30, g: 14, ingredientes: '1 lata atún al agua · 120 g garbanzos cocidos · rúcula · tomate cherry · limón', preparacion: 'Entibia los garbanzos. Mezcla todo con el atún y aliña con limón y una cucharadita de aceite.', etiquetas: ['sin_gluten', 'sin_lactosa'] },
]

const POR_MOMENTO: [MomentoComida, Receta[]][] = [
  ['desayuno', DESAYUNOS],
  ['almuerzo', ALMUERZOS],
  ['cena', CENAS],
]

function aOpcion(receta: Receta, momento: MomentoComida, i: number): OpcionDeMenu {
  return {
    id: `demo-${momento}-${String(i)}`,
    recetaId: `demo-receta-${momento}-${String(i)}`,
    nombre: receta.nombre,
    momento,
    kcal: receta.kcal,
    proteinaG: receta.p,
    carbohidratoG: receta.c,
    grasaG: receta.g,
    ingredientes: receta.ingredientes,
    preparacion: receta.preparacion,
    etiquetas: receta.etiquetas,
  }
}

export function menuDeDemostracion(): ComidaDelDia[] {
  return POR_MOMENTO.map(([momento, recetas]) => ({
    momento,
    opciones: recetas.map((r, i) => aOpcion(r, momento, i)),
    // El desayuno ya viene marcado: enseña el estado "ya comí" sin que nadie tenga que tocar nada.
    elegidaId: momento === 'desayuno' ? 'demo-desayuno-0' : null,
    fueraDelPlan: null,
  }))
}

/** El objetivo del menu que Flor le calculo. La formula la entrega ella: aqui es un dato, no un calculo. */
export const OBJETIVO_DE_DEMOSTRACION = {
  kcal: 1420,
  proteinaG: 104,
  carbohidratoG: 138,
  grasaG: 42,
}
